import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { API } from "../../lib/backend";

/**
 * /admin/system — super_admin only. Dangerous actions like full demo wipe.
 */
export default function SystemPage() {
  const [open, setOpen] = useState(false);
  const [confirmCheck, setConfirmCheck] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const canConfirm = confirmCheck && confirmText === "WIPE" && !busy;

  const doWipe = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/system/wipe-demo-data`,
        { confirm: "WIPE" },
        { withCredentials: true }
      );
      const stats = r.data?.deleted || {};
      const total = Object.values(stats).reduce((a, b) => a + (b || 0), 0);
      toast.success(`✓ Dati demo cancellati (${total} record).`);
      setOpen(false);
      setConfirmCheck(false);
      setConfirmText("");
      setTimeout(() => nav("/"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il wipe");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto p-8" data-testid="admin-system-page">
      <div className="mb-6">
        <h1
          className="text-[22px] font-semibold"
          style={{ color: "var(--text-primary)" }}
        >
          Sistema
        </h1>
        <p
          className="text-[13px] mt-1"
          style={{ color: "var(--text-muted)" }}
        >
          Azioni di manutenzione riservate al super-admin.
        </p>
      </div>

      <section
        className="rounded-lg p-6 bg-white"
        style={{ border: "1px solid #FECACA", boxShadow: "var(--shadow-sm)" }}
        data-testid="section-wipe-demo"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 mt-0.5" style={{ color: "#DC2626" }} />
          <div className="flex-1">
            <h2
              className="text-[15px] font-semibold"
              style={{ color: "#991B1B" }}
            >
              Reset demo data
            </h2>
            <p
              className="text-[13px] mt-1 max-w-[660px]"
              style={{ color: "var(--text-secondary)" }}
            >
              Cancella <strong>tutte</strong> le offerte, i clienti, gli allegati, i log e
              la cache idempotency. Lascia intatti utenti, listino articoli,
              configurazione aziendale e KB. Operazione <strong>irreversibile</strong>.
            </p>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="mt-4 inline-flex items-center gap-2 h-9 px-3 rounded-md text-[13px] font-medium text-white"
              style={{ background: "#DC2626" }}
              data-testid="wipe-demo-trigger"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Cancella tutte le offerte e clienti test
            </button>
          </div>
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.35)" }}
          role="dialog"
          data-testid="wipe-modal"
        >
          <div
            className="w-full max-w-[520px] bg-white rounded-lg p-6"
            style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
          >
            <h3
              className="text-[16px] font-semibold"
              style={{ color: "#991B1B" }}
            >
              Conferma cancellazione dati demo
            </h3>
            <p
              className="text-[13px] mt-2"
              style={{ color: "var(--text-secondary)" }}
            >
              Questa azione cancellerà tutte le offerte, i clienti e i log. Per
              procedere spunta la conferma e digita <strong>WIPE</strong>.
            </p>

            <label className="flex items-start gap-2 mt-4 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmCheck}
                onChange={(e) => setConfirmCheck(e.target.checked)}
                className="mt-1"
                data-testid="wipe-confirm-check"
              />
              <span
                className="text-[13px]"
                style={{ color: "var(--text-primary)" }}
              >
                Confermo di voler eliminare TUTTE le offerte e i clienti dal
                database.
              </span>
            </label>

            <div className="mt-3">
              <label
                className="text-[12px] font-medium block mb-1"
                style={{ color: "var(--text-secondary)" }}
              >
                Digita <strong>WIPE</strong> per confermare
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="WIPE"
                className="w-full h-9 px-3 rounded-md text-[13px] tq-cell-mono"
                style={{
                  border: "1px solid var(--border-subtle)",
                  outline: "none",
                }}
                data-testid="wipe-confirm-input"
                autoFocus
              />
            </div>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setConfirmCheck(false);
                  setConfirmText("");
                }}
                className="h-9 px-3 rounded-md text-[13px]"
                style={{
                  border: "1px solid var(--border-subtle)",
                  color: "var(--text-primary)",
                }}
                data-testid="wipe-cancel-btn"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={doWipe}
                disabled={!canConfirm}
                className="h-9 px-3 rounded-md text-[13px] font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "#DC2626" }}
                data-testid="wipe-confirm-btn"
              >
                {busy ? "Cancellazione…" : "Sì, cancella tutto"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
